import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateSetUnitsCompleted } from 'redux/slices/activeWorkoutV5'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'

export default function VolumeCompletedV5({ 
    kinesisWorkoutAssignmentType,
    groupIndividual = null,
    phaseIndex, 
    slotIndex, 
    exercise,
    setIndex, 
    set, 
    isCompleted, 
    isSkipped,
    showLabel = true
}) {
    const { urlWorkoutId } = useParams()

    const dispatch = useDispatch()

    const disableCardInputs = isCompleted === 1 || isSkipped === 1

    var setUnitsCompleted = 0
    if (kinesisWorkoutAssignmentType === 'group') setUnitsCompleted = set.setUnitsCompleted
    if (kinesisWorkoutAssignmentType === 'group-individual') setUnitsCompleted = groupIndividual?.workout?.phases[phaseIndex]?.slots[slotIndex]?.sets[setIndex]?.setUnitsCompleted
    if (kinesisWorkoutAssignmentType === 'individual') setUnitsCompleted = set.setUnitsCompleted

    var setUnitsAssigned = set?.setUnitsAssigned

    var groupIndividualId = null
    if (kinesisWorkoutAssignmentType === 'group-individual') groupIndividualId = groupIndividual?.id

    const [isScaling, setIsScaling] = useState(false)
    const [isInputBoxCurrentlyFocused, setIsInputBoxCurrentlyFocused] = useState(false)
    const [inputBoxValue, setInputBoxValue] = useState(setUnitsCompleted)

    // Increment/decrement functions

    const IncrementSetUnitsCompleted = () => {
        if (setUnitsCompleted < setUnitsAssigned) {

            const payload = {
                kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
                workoutId: urlWorkoutId,
                groupIndividualId: groupIndividualId,
                phaseIndex: phaseIndex,
                slotIndex: slotIndex,
                setIndex: setIndex,
                setUnitsCompleted: setUnitsCompleted + 1
            }

            dispatch(updateSetUnitsCompleted(payload))
        }

        TriggerScaleAnimation()
    }

    const DecrementSetUnitsCompleted = () => {
        if (setUnitsCompleted > 0) {

            const payload = {
                kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
                workoutId: urlWorkoutId,
                groupIndividualId: groupIndividualId,
                phaseIndex: phaseIndex,
                slotIndex: slotIndex,
                setIndex: setIndex,
                setUnitsCompleted: setUnitsCompleted - 1
            }

            dispatch(updateSetUnitsCompleted(payload))
        }

        TriggerScaleAnimation()
    }

    const TriggerScaleAnimation = () => {
        setIsScaling(true)
        setTimeout(() => setIsScaling(false), 200)
    }

    // Input box functions

    const HandleInputBoxFocus = () => {
        if (disableCardInputs) return
        setIsInputBoxCurrentlyFocused(true)
        setInputBoxValue('')
    }

    const HandleSetUnitsCompletedChange = (value) => {
        if (disableCardInputs) return

        // Allow empty string
        if (value === '') {
            setInputBoxValue('')
            return
        }

        // Only allow digits, max 2 characters
        const numericValue = value.replace(/[^0-9]/g, '')

        if (numericValue.length > 2) return

        // Only update local state - no Redux dispatching
        setInputBoxValue(numericValue)
    }

    const HandleSetUnitsCompletedBlur = () => {
        if (disableCardInputs) return

        // If input is empty, just reset focus state (don't update Redux)
        if (inputBoxValue === '') {
            setIsInputBoxCurrentlyFocused(false)
            return
        }

        // Parse and validate the input value
        const currentValue = parseInt(inputBoxValue, 10) || 0
        const clampedValue = Math.max(0, Math.min(currentValue, setUnitsAssigned))

        const payload = {
            kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
            workoutId: urlWorkoutId,
            groupIndividualId: groupIndividualId,
            phaseIndex: phaseIndex,
            slotIndex: slotIndex,
            setIndex: setIndex,
            setUnitsCompleted: clampedValue
        }

        dispatch(updateSetUnitsCompleted(payload))

        // Reset local state
        setInputBoxValue('')
        setIsInputBoxCurrentlyFocused(false)
    }

    if (isSkipped === 1) return null

    return (
        <div className='space-y-2'>
            {showLabel && (
                <div className='h-4 text-xs text-center text-gray-600'>
                    {exercise.setUnit === 'reps' && <>Reps</>}
                    {exercise.setUnit === 'seconds' && <>Seconds</>}
                </div>
            )}

            <div className='flex items-center justify-center'>
                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pr-1 py-4'
                        onClick={() => DecrementSetUnitsCompleted()}
                    >
                        <ChevronLeftIcon className='ml-auto w-10 h-10 text-gray-600' />
                    </div>
                }

                <div 
                    className={`
                        relative 
                        ${isScaling ? 'animate-scale-bounce' : ''}
                    `}
                >
                    <input 
                        type="text" 
                        inputMode="numeric"
                        className='w-20 h-20 rounded-xl flex items-center justify-center text-4xl text-center text-gray-900 pb-3 border border-gray-400 bg-white'
                        value={isInputBoxCurrentlyFocused ? inputBoxValue : String(setUnitsCompleted)} 
                        disabled={disableCardInputs}
                        onFocus={HandleInputBoxFocus}
                        onChange={(event) => HandleSetUnitsCompletedChange(event.target.value)}
                        onBlur={HandleSetUnitsCompletedBlur}
                    />

                    <div className='absolute bottom-2 left-0 w-full text-center text-xs text-gray-800'>
                        {exercise.setUnit === 'reps' &&
                            <>
                                {exercise.lateral === 'unilateral' && <>per side</>}
                                {exercise.lateral === 'bilateral' && <>total</>}
                            </>
                        }
                        {exercise.setUnit === 'seconds' && 
                            <>
                                {exercise.lateral === 'unilateral' && <>sec / side</>}
                                {exercise.lateral === 'bilateral' && <>seconds</>}
                            </>
                        }
                    </div>
                </div>

                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pl-1 py-4'
                        onClick={() => IncrementSetUnitsCompleted()}
                    >
                        <ChevronRightIcon className='mr-auto w-10 h-10 text-gray-600' />
                    </div>
                }
            </div>
        </div>
    )
}
